/**
 * Social Food Gifting & Requesting — UI Controller
 *
 * Handles all client-side interactions for:
 *  - Gift Order modal (send a meal gift)
 *  - Meal Request modal (ask a friend to buy you food)
 *  - Dashboard panels: Gifts Received, My Requests, Requests For Me
 *  - Friend picker (search clients by name/email)
 *  - Privacy opt-in toggle (allowFriendRequests)
 *
 * Depends on:
 *  - window.db                 (Firestore instance from firebase-sdk-bootstrap.js)
 *  - window.firebase           (legacy compat SDK namespace)
 *  - window.__firestoreModular (modular Firestore functions, set by bootstrap)
 *  - window.currentClientData  (signed-in client profile object from app.js)
 *  - window.showNotification   (global toast helper from app.js)
 *  - Stripe.js                 (loaded in index.html)
 */

import {
  createGiftOrder,
  cancelGiftOrder,
  acceptGiftOrder,
  listenToSentGifts,
  listenToReceivedGifts,
  GIFT_ORDER_STATUSES,
  GIFT_PAYMENT_STATUSES,
} from "./giftOrders.js";

import {
  createMealRequest,
  cancelMealRequest,
  declineMealRequest,
  fulfillMealRequest,
  listenToSentRequests,
  listenToIncomingRequests,
  searchFriends,
  MEAL_REQUEST_STATUSES,
} from "./socialMealRequests.js";

import {
  listenToGiftNotifications,
  listenToMealRequestNotifications,
} from "./notifications.js";

// ─── Module-level state ───────────────────────────────────────────────────────

let _db = null;
let _currentUser = null;         // Firebase Auth user
let _currentClientData = null;   // Firestore client profile

// Active onSnapshot unsubscribe handles
let _unsubReceivedGifts = null;
let _unsubSentGifts = null;
let _unsubIncomingRequests = null;
let _unsubSentRequests = null;
let _unsubGiftNotifications = null;
let _unsubRequestNotifications = null;

// State for the currently open gift modal
let _giftContext = null;  // { menuItemId, title, price, quantity, providerId, providerName, currency }

// State for the currently open meal request modal
let _requestContext = null;

// Debounce timer for friend picker
let _friendSearchTimer = null;
let _giftPaymentStripe = null;
let _giftPaymentElements = null;
let _giftPaymentCard = null;
let _giftPaymentMountedKey = "";

function _getGiftPaymentRuntimeConfig() {
  const publishableKey = String(window.__STRIPE_PUBLISHABLE_KEY__ || "").trim();
  const functionsBase = String(window.__FUNCTIONS_BASE_URL__ || "https://us-central1-what-to-eat-e5ce9.cloudfunctions.net").trim();
  const hasValidPublishableKey = /^pk_(test|live)_/i.test(publishableKey);
  return { publishableKey, functionsBase, hasValidPublishableKey };
}

function _setGiftPaymentAvailabilityUi() {
  const btn = document.getElementById("sendGiftBtn");
  const statusEl = document.getElementById("giftOrderStatus");
  if (!btn || !statusEl) return;

  const cfg = _getGiftPaymentRuntimeConfig();
  const hasPaymentCollector = typeof window.getGiftOrderPaymentMethod === "function";
  const giftPaymentEnabled = cfg.hasValidPublishableKey && hasPaymentCollector;

  btn.disabled = !giftPaymentEnabled;
  if (giftPaymentEnabled) {
    statusEl.style.display = "none";
    statusEl.textContent = "";
    return;
  }

  statusEl.style.display = "block";
  statusEl.style.color = "#b7791f";
  statusEl.textContent = "Gift card checkout is temporarily unavailable. Please try again later.";
}

async function _ensureGiftPaymentCardMounted() {
  const cfg = _getGiftPaymentRuntimeConfig();
  if (!cfg.hasValidPublishableKey) {
    throw new Error("Stripe publishable key is not configured.");
  }

  if (!window.Stripe) {
    await new Promise((resolve, reject) => {
      const existing = document.getElementById("stripe-js");
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")), { once: true });
        return;
      }
      const script = document.createElement("script");
      script.id = "stripe-js";
      script.src = "https://js.stripe.com/v3/";
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Stripe.js"));
      document.head.appendChild(script);
    });
  }
  if (!window.Stripe) {
    throw new Error("Stripe.js is not loaded. Please refresh and try again.");
  }

  if (!_giftPaymentStripe || _giftPaymentMountedKey !== cfg.publishableKey) {
    _giftPaymentStripe = window.Stripe(cfg.publishableKey);
    _giftPaymentElements = _giftPaymentStripe.elements();
    _giftPaymentCard = _giftPaymentElements.create("card");
    _giftPaymentMountedKey = cfg.publishableKey;
  }

  const mountTarget = document.getElementById("giftPaymentElement");
  if (!mountTarget) {
    throw new Error("Gift payment input is unavailable.");
  }
  if (!_giftPaymentCard) {
    throw new Error("Gift payment form failed to initialize.");
  }

  if (!mountTarget.dataset.mounted) {
    mountTarget.innerHTML = "";
    _giftPaymentCard.mount(mountTarget);
    mountTarget.dataset.mounted = "true";
  }
}

window.getGiftOrderPaymentMethod = async function getGiftOrderPaymentMethod({ stripe }) {
  if (!_currentClientData || !_giftPaymentCard) {
    throw new Error("Gift payment form is not ready.");
  }
  const stripeClient = stripe || _giftPaymentStripe;
  if (!stripeClient) {
    throw new Error("Stripe client is not ready.");
  }

  const paymentMethodResult = await stripeClient.createPaymentMethod({
    type: "card",
    card: _giftPaymentCard,
    billing_details: {
      name: _currentClientData.displayName || _currentClientData.name || "",
      email: _currentClientData.email || "",
    },
  });
  if (paymentMethodResult.error) {
    throw new Error(paymentMethodResult.error.message || "Failed to collect card details.");
  }
  const paymentMethodId = paymentMethodResult.paymentMethod && paymentMethodResult.paymentMethod.id
    ? paymentMethodResult.paymentMethod.id
    : "";
  if (!paymentMethodId) {
    throw new Error("Failed to create a card payment method.");
  }
  return { paymentMethodId };
};

// ─── Bootstrap ────────────────────────────────────────────────────────────────

/**
 * Call this once after auth state resolves with a signed-in client.
 */
export function initSocialFeatures(db, user, clientData) {
  _db = db;
  _currentUser = user;
  _currentClientData = clientData;

  // Start real-time listeners
  _startListeners();

  // Populate privacy toggle from profile
  _syncPrivacyToggle();

  console.log("[social] Social features initialised for", user.uid);
}

/**
 * Call when the client signs out to clean up listeners.
 */
export function teardownSocialFeatures() {
  [_unsubReceivedGifts, _unsubSentGifts, _unsubIncomingRequests,
    _unsubSentRequests, _unsubGiftNotifications, _unsubRequestNotifications]
    .forEach((fn) => { try { if (fn) fn(); } catch (_) {} });

  _db = null;
  _currentUser = null;
  _currentClientData = null;
  console.log("[social] Listeners torn down.");
}

function _startListeners() {
  const uid = _currentUser.uid;

  // Received gifts → refresh panel + badge
  _unsubReceivedGifts = listenToReceivedGifts(_db, uid, (gifts) => {
    _renderReceivedGifts(gifts);
    _updateBadge("giftsReceivedBadge",
      gifts.filter((g) => g.paymentStatus === GIFT_PAYMENT_STATUSES.PAID && g.status === GIFT_ORDER_STATUSES.PENDING).length
    );
  });

  // Sent gifts → refresh panel
  _unsubSentGifts = listenToSentGifts(_db, uid, (gifts) => {
    _renderSentGifts(gifts);
  });

  // Incoming meal requests (friends want me to pay)
  _unsubIncomingRequests = listenToIncomingRequests(_db, uid, (requests) => {
    _renderIncomingRequests(requests);
    _updateBadge("requestsForMeBadge", requests.length);
  });

  // My own outgoing requests
  _unsubSentRequests = listenToSentRequests(_db, uid, (requests) => {
    _renderSentRequests(requests);
  });

  // In-app banner notifications
  _unsubGiftNotifications = listenToGiftNotifications(_db, uid);
  _unsubRequestNotifications = listenToMealRequestNotifications(_db, uid);
}

// ─── Privacy toggle ───────────────────────────────────────────────────────────

function _syncPrivacyToggle() {
  const checkbox = document.getElementById("clientAllowFriendRequests");
  if (!checkbox || !_currentClientData) return;
  checkbox.checked = !!_currentClientData.allowFriendRequests;
}

/**
 * Called from the profile save handler in app.js to include the privacy flag.
 * Returns the value of the opt-in checkbox.
 */
export function getPrivacyOptIn() {
  const cb = document.getElementById("clientAllowFriendRequests");
  return cb ? cb.checked : false;
}

// ─── Badge helper ─────────────────────────────────────────────────────────────

function _updateBadge(id, count) {
  const el = document.getElementById(id);
  if (!el) return;
  if (count > 0) {
    el.textContent = count > 99 ? "99+" : String(count);
    el.classList.remove("hidden");
  } else {
    el.classList.add("hidden");
  }
}

// ─── Dashboard panel renderers ────────────────────────────────────────────────

function _statusBadge(status) {
  const colours = {
    pending: "#e2a84a", accepted: "#38a169", preparing: "#3182ce",
    ready: "#805ad5", completed: "#2d3748", cancelled: "#e53e3e",
    open: "#38a169", declined: "#e53e3e", fulfilled: "#2d3748", expired: "#a0aec0",
  };
  const bg = colours[status] || "#a0aec0";
  return `<span style="background:${bg}; color:#fff; padding:2px 8px; border-radius:12px; font-size:0.8rem; font-weight:600; text-transform:uppercase;">${status}</span>`;
}

function _formatTs(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString();
}

function _card(innerHtml) {
  return `<div class="social-card" style="background:#fff; border:1px solid #e2e8f0; border-radius:10px; padding:14px; margin-bottom:12px; box-shadow:0 1px 3px rgba(0,0,0,.06);">${innerHtml}</div>`;
}

function _renderReceivedGifts(gifts) {
  const el = document.getElementById("giftsReceivedList");
  if (!el) return;
  if (!gifts.length) { el.innerHTML = '<p class="empty-state-msg">No gifts received yet.</p>'; return; }

  el.innerHTML = gifts.map((g) => {
    const itemsSummary = (g.items || []).map((i) => `${i.title} ×${i.quantity}`).join(", ");
    const isPending = g.paymentStatus === "paid" && g.status === GIFT_ORDER_STATUSES.PENDING;
    const acceptBtn = isPending
      ? `<button onclick="handleAcceptGift('${g.id}')" style="background:#38a169; color:#fff; border:none; padding:6px 16px; border-radius:6px; cursor:pointer; margin-top:8px;">✅ Accept Gift</button>`
      : "";
    return _card(`
      <div style="display:flex; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; gap:6px;">
        <div>
          <strong>🎁 From ${g.senderName || "Someone"}</strong><br>
          <span style="color:#555; font-size:0.9rem;">${itemsSummary} — <em>${g.providerName || ""}</em></span><br>
          <span style="color:#777; font-size:0.85rem;">${g.message ? `"${g.message}"` : ""}</span>
        </div>
        <div style="text-align:right;">
          ${_statusBadge(g.status)}<br>
          <span style="font-weight:bold; color:#2b6cb0;">$${(g.totalAmount || 0).toFixed(2)} ${g.currency || "USD"}</span><br>
          <small style="color:#999;">${_formatTs(g.createdAt)}</small>
        </div>
      </div>
      ${acceptBtn}
    `);
  }).join("");
}

function _renderSentGifts(gifts) {
  // Sent gifts are shown under "My Requests" panel for now; no dedicated panel yet
  // but the data is ready for future expansion
}

function _renderIncomingRequests(requests) {
  const el = document.getElementById("requestsForMeList");
  if (!el) return;
  if (!requests.length) { el.innerHTML = '<p class="empty-state-msg">No pending requests from friends.</p>'; return; }

  el.innerHTML = requests.map((r) => {
    const itemsSummary = (r.items || []).map((i) => `${i.title} ×${i.quantity}`).join(", ");
    const expiresText = r.expiresAt
      ? `Expires: ${_formatTs(r.expiresAt)}`
      : "";
    return _card(`
      <div style="display:flex; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; gap:6px;">
        <div>
          <strong>🍔 ${r.requesterName || "A friend"} wants you to buy them a meal</strong><br>
          <span style="color:#555; font-size:0.9rem;">${itemsSummary} — <em>${r.providerName || ""}</em></span><br>
          <span style="color:#777; font-size:0.85rem;">${r.message ? `"${r.message}"` : ""}</span><br>
          <small style="color:#999;">${expiresText}</small>
        </div>
        <div style="text-align:right;">
          ${_statusBadge(r.status)}<br>
          <span style="font-weight:bold; color:#2b6cb0;">$${(r.totalAmount || 0).toFixed(2)} ${r.currency || "USD"}</span><br>
          <small style="color:#999;">${_formatTs(r.createdAt)}</small>
        </div>
      </div>
      <div style="display:flex; gap:8px; margin-top:8px; flex-wrap:wrap;">
        <button onclick="handleAcceptMealRequest('${r.id}')"
          style="background:#2b6cb0; color:#fff; border:none; padding:6px 16px; border-radius:6px; cursor:pointer;">
          ✅ Accept &amp; Pay
        </button>
        <button onclick="handleDeclineMealRequest('${r.id}')"
          style="background:#e53e3e; color:#fff; border:none; padding:6px 14px; border-radius:6px; cursor:pointer;">
          ❌ Decline
        </button>
      </div>
    `);
  }).join("");
}

function _renderSentRequests(requests) {
  const el = document.getElementById("myRequestsList");
  if (!el) return;
  if (!requests.length) { el.innerHTML = '<p class="empty-state-msg">You haven\'t sent any meal requests yet.</p>'; return; }

  el.innerHTML = requests.map((r) => {
    const itemsSummary = (r.items || []).map((i) => `${i.title} ×${i.quantity}`).join(", ");
    const canCancel = r.status === MEAL_REQUEST_STATUSES.OPEN;
    const cancelBtn = canCancel
      ? `<button onclick="handleCancelMealRequest('${r.id}')" style="background:#e53e3e; color:#fff; border:none; padding:5px 14px; border-radius:6px; cursor:pointer; margin-top:8px;">Cancel Request</button>`
      : "";
    return _card(`
      <div style="display:flex; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; gap:6px;">
        <div>
          <strong>🍔 Request to ${r.targetFriendEmail || "friend"}</strong><br>
          <span style="color:#555; font-size:0.9rem;">${itemsSummary} — <em>${r.providerName || ""}</em></span><br>
          <span style="color:#777; font-size:0.85rem;">${r.message ? `"${r.message}"` : ""}</span>
        </div>
        <div style="text-align:right;">
          ${_statusBadge(r.status)}<br>
          <span style="font-weight:bold; color:#2b6cb0;">$${(r.totalAmount || 0).toFixed(2)} ${r.currency || "USD"}</span><br>
          <small style="color:#999;">${_formatTs(r.createdAt)}</small>
        </div>
      </div>
      ${cancelBtn}
    `);
  }).join("");
}

// ─── Gift Order Modal ─────────────────────────────────────────────────────────

/**
 * Open the "Send a Meal Gift" modal for a menu item.
 * Called from menu item card buttons.
 */
export function openGiftOrderModal(menuItem) {
  if (!_currentUser) {
    if (typeof window.showNotification === "function") {
      window.showNotification("Please log in as a client to send meal gifts.", "error");
    }
    return;
  }

  _giftContext = { ...menuItem };

  document.getElementById("giftProviderName").textContent = menuItem.providerName || "";
  document.getElementById("giftItemTitle").textContent = menuItem.title || menuItem.foodName || "";
  document.getElementById("giftItemPrice").textContent = `$${(menuItem.price || 0).toFixed(2)}`;
  document.getElementById("giftQuantity").value = 1;
  document.getElementById("giftMessage").value = "";
  document.getElementById("giftRecipientId").value = "";
  document.getElementById("giftRecipientDisplay").value = "";
  document.getElementById("giftFriendSearch").value = "";
  document.getElementById("giftFriendResults").classList.add("hidden");
  document.getElementById("giftFriendResults").innerHTML = "";
  const giftOrderStatus = document.getElementById("giftOrderStatus");
  giftOrderStatus.style.display = "none";
  giftOrderStatus.textContent = "";
  const sendGiftBtn = document.getElementById("sendGiftBtn");
  if (sendGiftBtn) sendGiftBtn.disabled = false;
  _updateGiftTotal();
  _ensureGiftPaymentCardMounted()
    .then(() => _setGiftPaymentAvailabilityUi())
    .catch((err) => {
      const statusEl = document.getElementById("giftOrderStatus");
      if (statusEl) {
        statusEl.style.display = "block";
        statusEl.style.color = "#e53e3e";
        statusEl.textContent = err && err.message ? err.message : "Card payment setup failed.";
      }
      if (sendGiftBtn) sendGiftBtn.disabled = true;
    });

  document.getElementById("giftOrderModal").style.display = "flex";
}

window.openGiftOrderModal = openGiftOrderModal;

window.closeGiftOrderModal = function () {
  document.getElementById("giftOrderModal").style.display = "none";
  _giftContext = null;
};

window.updateGiftTotal = function () { _updateGiftTotal(); };

function _updateGiftTotal() {
  if (!_giftContext) return;
  const qty = parseInt(document.getElementById("giftQuantity").value || "1", 10);
  const total = (_giftContext.price || 0) * Math.max(1, qty);
  document.getElementById("giftTotalDisplay").textContent = `Total: $${total.toFixed(2)}`;
}

window.submitGiftOrder = async function () {
  if (!_db || !_currentUser || !_currentClientData) {
    window.showNotification?.("Please log in to send a gift.", "error");
    return;
  }

  const paymentRuntime = _getGiftPaymentRuntimeConfig();
  if (!paymentRuntime.hasValidPublishableKey || typeof window.getGiftOrderPaymentMethod !== "function") {
    window.showNotification?.("Gift card checkout is temporarily unavailable. Please try again later.", "error");
    _setGiftPaymentAvailabilityUi();
    return;
  }

  const recipientId = document.getElementById("giftRecipientId").value.trim();
  if (!recipientId) {
    window.showNotification?.("Please select a recipient friend.", "error");
    return;
  }

  const qty = Math.max(1, parseInt(document.getElementById("giftQuantity").value || "1", 10));
  const message = document.getElementById("giftMessage").value.trim();
  const totalAmount = (_giftContext.price || 0) * qty;
  const currency = _giftContext.currency || "USD";

  const statusEl = document.getElementById("giftOrderStatus");
  const btn = document.getElementById("sendGiftBtn");

  statusEl.style.display = "block";
  statusEl.style.color = "#555";
  statusEl.textContent = "Creating gift order…";
  btn.disabled = true;

  try {
    const giftOrderId = await createGiftOrder(_db, {
      senderId: _currentUser.uid,
      senderName: _currentClientData.displayName || _currentClientData.name || "",
      recipientId,
      recipientEmail: document.getElementById("giftRecipientDisplay").dataset.email || "",
      providerId: _giftContext.providerId,
      providerName: _giftContext.providerName || "",
      items: [{ menuItemId: _giftContext.id, title: _giftContext.title || _giftContext.foodName || "", quantity: qty, price: _giftContext.price || 0 }],
      totalAmount,
      currency,
      message,
      compliance: {},
    });

    // Initiate Stripe payment via Cloud Function
    const idToken = await _currentUser.getIdToken();
    const piRes = await fetch(`${paymentRuntime.functionsBase}/processGiftOrderPayment`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ giftOrderId }),
    });
    const piData = await piRes.json();

    if (!piData.ok || !piData.clientSecret) {
      throw new Error(piData.error || "Payment initiation failed.");
    }

    // Use Stripe.js to confirm payment
    const stripe = window.Stripe ? window.Stripe(paymentRuntime.publishableKey) : null;
    if (!stripe) throw new Error("Stripe.js is not loaded. Please refresh and try again.");

    const paymentMethodResult = await window.getGiftOrderPaymentMethod({
      stripe,
      clientSecret: piData.clientSecret,
      giftOrderId,
    });
    const paymentMethodId = typeof paymentMethodResult === "string"
      ? paymentMethodResult
      : paymentMethodResult && typeof paymentMethodResult.paymentMethodId === "string"
        ? paymentMethodResult.paymentMethodId
        : "";
    if (!paymentMethodId) {
      throw new Error("Gift payment method is not ready. Please try again.");
    }

    statusEl.textContent = "Redirecting to payment…";
    const { error: stripeError } = await stripe.confirmCardPayment(piData.clientSecret, {
      payment_method: paymentMethodId,
    });

    if (stripeError) throw new Error(stripeError.message);

    statusEl.style.color = "#38a169";
    statusEl.textContent = "✅ Payment received. Final confirmation is processing now.";
    window.showNotification?.("🎁 Payment received! Gift will appear as confirmed shortly.", "success");

    setTimeout(() => window.closeGiftOrderModal(), 2500);
  } catch (err) {
    console.error("[submitGiftOrder]", err);
    statusEl.style.color = "#e53e3e";
    statusEl.textContent = `Error: ${err.message}`;
    btn.disabled = false;
  }
};

// ─── Meal Request Modal ───────────────────────────────────────────────────────

export function openMealRequestModal(menuItem) {
  if (!_currentUser) {
    window.showNotification?.("Please log in as a client to send meal requests.", "error");
    return;
  }

  _requestContext = { ...menuItem };

  document.getElementById("requestItemTitle").textContent = menuItem.title || menuItem.foodName || "";
  document.getElementById("requestProviderName").textContent = menuItem.providerName || "";
  document.getElementById("requestItemTitleDisplay").textContent = menuItem.title || menuItem.foodName || "";
  document.getElementById("requestItemPrice").textContent = `$${(menuItem.price || 0).toFixed(2)}`;
  document.getElementById("requestQuantity").value = 1;
  document.getElementById("requestMessage").value = "";
  document.getElementById("requestTargetFriendId").value = "";
  document.getElementById("requestFriendDisplay").value = "";
  document.getElementById("requestFriendSearch").value = "";
  document.getElementById("requestFriendResults").classList.add("hidden");
  document.getElementById("requestFriendResults").innerHTML = "";
  document.getElementById("mealRequestStatus").style.display = "none";
  _updateRequestTotal();

  document.getElementById("mealRequestModal").style.display = "flex";
}

window.openMealRequestModal = openMealRequestModal;

window.closeMealRequestModal = function () {
  document.getElementById("mealRequestModal").style.display = "none";
  _requestContext = null;
};

window.updateRequestTotal = function () { _updateRequestTotal(); };

function _updateRequestTotal() {
  if (!_requestContext) return;
  const qty = parseInt(document.getElementById("requestQuantity").value || "1", 10);
  const total = (_requestContext.price || 0) * Math.max(1, qty);
  document.getElementById("requestTotalDisplay").textContent = `Total: $${total.toFixed(2)}`;
}

window.submitMealRequest = async function () {
  if (!_db || !_currentUser || !_currentClientData) {
    window.showNotification?.("Please log in to send a meal request.", "error");
    return;
  }

  const targetFriendId = document.getElementById("requestTargetFriendId").value.trim();
  if (!targetFriendId) {
    window.showNotification?.("Please select a friend to send the request to.", "error");
    return;
  }

  const qty = Math.max(1, parseInt(document.getElementById("requestQuantity").value || "1", 10));
  const message = document.getElementById("requestMessage").value.trim();
  const totalAmount = (_requestContext.price || 0) * qty;

  const statusEl = document.getElementById("mealRequestStatus");
  const btn = document.getElementById("sendRequestBtn");

  statusEl.style.display = "block";
  statusEl.style.color = "#555";
  statusEl.textContent = "Sending request…";
  btn.disabled = true;

  try {
    await createMealRequest(_db, {
      requesterId: _currentUser.uid,
      requesterName: _currentClientData.displayName || _currentClientData.name || "",
      targetFriendId,
      targetFriendEmail: document.getElementById("requestFriendDisplay").dataset.email || "",
      providerId: _requestContext.providerId,
      providerName: _requestContext.providerName || "",
      items: [{ menuItemId: _requestContext.id, title: _requestContext.title || _requestContext.foodName || "", quantity: qty, price: _requestContext.price || 0 }],
      totalAmount,
      currency: _requestContext.currency || "USD",
      message,
      expiresInHours: 24,
    });

    statusEl.style.color = "#38a169";
    statusEl.textContent = "🍔 Meal request sent! Your friend has been notified.";
    window.showNotification?.("🍔 Meal request sent!", "success");

    setTimeout(() => window.closeMealRequestModal(), 2000);
  } catch (err) {
    console.error("[submitMealRequest]", err);
    statusEl.style.color = "#e53e3e";
    statusEl.textContent = `Error: ${err.message}`;
    btn.disabled = false;
  }
};

// ─── Dashboard action handlers ────────────────────────────────────────────────

window.handleAcceptGift = async function (giftOrderId) {
  try {
    await acceptGiftOrder(_db, giftOrderId);
    window.showNotification?.("✅ Gift accepted!", "success");
  } catch (err) {
    window.showNotification?.("Failed to accept gift: " + err.message, "error");
  }
};

window.handleAcceptMealRequest = async function (requestId) {
  try {
    const idToken = await _currentUser.getIdToken();
    const functionsBase = window.__FUNCTIONS_BASE_URL__ || "https://us-central1-what-to-eat-e5ce9.cloudfunctions.net";

    const res = await fetch(`${functionsBase}/acceptSocialMealRequest`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ requestId }),
    });
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || "Failed to accept request.");

    window.showNotification?.("✅ Request accepted! Gift order created — please complete payment.", "success");
    // TODO: open payment flow for the new giftOrderId (data.giftOrderId)
  } catch (err) {
    window.showNotification?.("Failed to accept request: " + err.message, "error");
  }
};

window.handleDeclineMealRequest = async function (requestId) {
  try {
    await declineMealRequest(_db, requestId);
    window.showNotification?.("❌ Request declined.", "info");
  } catch (err) {
    window.showNotification?.("Failed to decline request: " + err.message, "error");
  }
};

window.handleCancelMealRequest = async function (requestId) {
  try {
    await cancelMealRequest(_db, requestId);
    window.showNotification?.("Request cancelled.", "info");
  } catch (err) {
    window.showNotification?.("Failed to cancel request: " + err.message, "error");
  }
};

// ─── Friend Picker ────────────────────────────────────────────────────────────

function _debounceSearch(fn, ms = 350) {
  if (_friendSearchTimer) clearTimeout(_friendSearchTimer);
  _friendSearchTimer = setTimeout(fn, ms);
}

function _renderFriendResults(results, dropdownId, hiddenInputId, displayInputId) {
  const dropdown = document.getElementById(dropdownId);
  if (!results.length) {
    dropdown.innerHTML = '<div style="padding:8px 12px; color:#777; font-size:0.9rem;">No friends found with that name or email. Make sure they have enabled friend discovery in their profile.</div>';
    dropdown.classList.remove("hidden");
    return;
  }

  dropdown.innerHTML = results.map((f) =>
    `<div class="friend-picker-item" data-uid="${f.uid}" data-email="${f.email}"
      style="padding:8px 12px; cursor:pointer; border-bottom:1px solid #eee; font-size:0.95rem;"
      onmousedown="selectFriendPicker('${dropdownId}','${hiddenInputId}','${displayInputId}','${f.uid}','${f.displayName}','${f.email}')">
      <strong>${f.displayName}</strong> <small style="color:#777;">${f.email}</small>
    </div>`
  ).join("");
  dropdown.classList.remove("hidden");
}

window.searchFriendPickerGift = function (value) {
  _debounceSearch(async () => {
    if (!_db || !value.trim()) return;
    const results = await searchFriends(_db, value, _currentUser?.uid).catch(() => []);
    _renderFriendResults(results, "giftFriendResults", "giftRecipientId", "giftRecipientDisplay");
  });
};

window.searchFriendPickerRequest = function (value) {
  _debounceSearch(async () => {
    if (!_db || !value.trim()) return;
    const results = await searchFriends(_db, value, _currentUser?.uid).catch(() => []);
    _renderFriendResults(results, "requestFriendResults", "requestTargetFriendId", "requestFriendDisplay");
  });
};

window.selectFriendPicker = function (dropdownId, hiddenInputId, displayInputId, uid, displayName, email) {
  document.getElementById(hiddenInputId).value = uid;
  const displayEl = document.getElementById(displayInputId);
  displayEl.value = `${displayName} (${email})`;
  displayEl.dataset.email = email;
  document.getElementById(dropdownId).classList.add("hidden");
};

// Close friend picker dropdowns on outside click
document.addEventListener("click", (e) => {
  ["giftFriendResults", "requestFriendResults"].forEach((id) => {
    const el = document.getElementById(id);
    if (el && !el.contains(e.target)) el.classList.add("hidden");
  });
});

// Close modals on backdrop click
document.addEventListener("click", (e) => {
  ["giftOrderModal", "mealRequestModal"].forEach((id) => {
    const modal = document.getElementById(id);
    if (modal && e.target === modal) modal.style.display = "none";
  });
});

// ─── Global bridge for app.js ─────────────────────────────────────────────────
// app.js calls these after auth state changes so that listeners start/stop cleanly.
window.initSocialFeatures = initSocialFeatures;
window.teardownSocialFeatures = teardownSocialFeatures;
window.getPrivacyOptIn = getPrivacyOptIn;
